import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import { mdiContentCopy, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { WizardStepHeader } from './WizardStepHeader'
import { METER_CHOICES, type MeterTypeCode } from '../lib/meters'

interface Building {
  id: string
  name: string
}

interface BuildingCreateFormProps {
  buildingGroupName: string
  /**
   * `createOutdoor` — the "this site has outdoor areas" checkbox, checked by
   * default. Submitting the step is the confirmation (spec 2026-07-09 /
   * #1071, no speculative assets): unticking it is a normal outcome and
   * `AssetCreateWizard.handleBuildingsNext` creates nothing in that case
   * (spec 2026-09-13-05 §3, §0 — the entity is optional, never mandatory).
   *
   * `meterTypeCodes` — the general meters to create on each building
   * (spec 2026-10-02-02), electricity and cold water checked by default.
   */
  onNext: (buildings: Omit<Building, 'id'>[], createOutdoor: boolean, meterTypeCodes: MeterTypeCode[]) => void
  onBack: () => void
  onCancel: () => void
}

export function BuildingCreateForm({ buildingGroupName, onNext, onBack, onCancel }: BuildingCreateFormProps) {
  const { t } = useTranslation('assets')
  const { t: tCommon } = useTranslation('common')
  const [buildings, setBuildings] = useState<Building[]>([
    { id: crypto.randomUUID(), name: `${t('create_building.buildings.default_prefix')}A` }
  ])
  const [createOutdoor, setCreateOutdoor] = useState(true)
  const [meterTypeCodes, setMeterTypeCodes] = useState<MeterTypeCode[]>(
    METER_CHOICES.filter(m => m.buildingDefault).map(m => m.code)
  )

  const toggleMeter = (code: MeterTypeCode, checked: boolean) => {
    setMeterTypeCodes(prev => (checked ? [...prev, code] : prev.filter(c => c !== code)))
  }

  const addBuilding = () => {
    setBuildings(prev => {
      const nextLetter = String.fromCharCode(65 + prev.length) // A, B, C, D, etc.
      return [
        ...prev,
        { id: crypto.randomUUID(), name: `${t('create_building.buildings.default_prefix')}${nextLetter}` }
      ]
    })
  }

  const removeBuilding = (id: string) => {
    if (buildings.length > 1) {
      setBuildings(prev => prev.filter(b => b.id !== id))
    }
  }

  const updateBuildingName = (id: string, name: string) => {
    setBuildings(prev => prev.map(b => 
      b.id === id ? { ...b, name } : b
    ))
  }


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const validBuildings = buildings.filter(b => b.name.trim())
    if (validBuildings.length > 0) {
      onNext(validBuildings.map(({ id: _, ...rest }) => ({ ...rest })), createOutdoor, meterTypeCodes)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-lg p-6">
        <WizardStepHeader step={2} title={t('create_building.buildings.title')} />

        <div className="mb-6">
          <p className="text-muted-foreground mb-2">{t('create_building.buildings.site_label', { name: buildingGroupName })}</p>
          <p className="text-sm text-muted-foreground">
            {t('create_building.buildings.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="building-create-form">
          <div>
            <h3 className="text-lg font-medium mb-4">{t('create_building.buildings.section_title')}</h3>
            
            <div className="space-y-4">
              {buildings.map((building) => (
                <div key={building.id} className="flex items-center gap-4 p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <Input
                      type="text"
                      value={building.name}
                      onChange={(e) => updateBuildingName(building.id, e.target.value)}
                      placeholder={t('create_building.buildings.name_placeholder')}
                      className="w-full"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newBuilding = {
                          id: crypto.randomUUID(),
                          name: building.name
                        }
                        setBuildings(prev => [...prev, newBuilding])
                      }}
                    >
                      <AppIcon path={mdiContentCopy} size="sm" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeBuilding(building.id)}
                      disabled={buildings.length === 1}
                    >
                      <AppIcon path={mdiTrashCanOutline} size="sm" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              className="mt-4 text-primary border-primary hover:bg-primary/10"
              onClick={addBuilding}
            >
              <AppIcon path={mdiPlus} size="sm" className="mr-2" />
              {t('create_building.buildings.add')}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="wizard-outdoor-checkbox"
              checked={createOutdoor}
              onCheckedChange={(checked) => setCreateOutdoor(Boolean(checked))}
              data-testid="wizard-outdoor-checkbox"
            />
            <Label htmlFor="wizard-outdoor-checkbox" className="cursor-pointer font-normal">
              {t('create_building.buildings.outdoor_checkbox')}
            </Label>
          </div>

          <fieldset className="space-y-2" data-testid="wizard-meters">
            <legend className="text-sm font-medium mb-1">{t('create_building.buildings.meters_title')}</legend>
            <p className="text-sm text-muted-foreground">{t('create_building.buildings.meters_description')}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
              {METER_CHOICES.map(({ code }) => (
                <div key={code} className="flex items-center gap-2">
                  <Checkbox
                    id={`wizard-meter-${code}`}
                    checked={meterTypeCodes.includes(code)}
                    onCheckedChange={(checked) => toggleMeter(code, Boolean(checked))}
                    data-testid={`wizard-meter-${code}`}
                  />
                  <Label htmlFor={`wizard-meter-${code}`} className="cursor-pointer font-normal">
                    {t(`meters.types.${code}`)}
                  </Label>
                </div>
              ))}
            </div>
          </fieldset>

          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
            >
              {tCommon('actions.back')}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={buildings.filter(b => b.name.trim()).length === 0}
                className="bg-primary hover:bg-primary/90"
              >
                {tCommon('actions.next')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}